import { IndexedDbCache } from './content/cache'
import { asContentError, type ContentError } from './content/errors'
import { GiteeContentSource } from './content/gitee'
import { HttpContentSource } from './content/http'
import { loadContent } from './content/loader'
import {
  DEFAULT_BUNDLE,
  DEFAULT_REF,
  loadRepoConfig,
  REPO_STORAGE_KEY,
  resolveRepoConfig,
  saveRepoConfig,
  type RepoConfig,
  type RepoDraft,
} from './content/repo-config'
import type { ContentSource, LoadedContent } from './content/types'
import type { Plan, QueueItem } from '@kc/core/review/plan'
import { cardOf, itemScoring } from '@kc/core/review/plan'
import { currentOf, IDLE, type SessionState } from '@kc/core/review/session'
import type { Rating } from '@kc/core/review/scheduling'
import { registerSW } from 'virtual:pwa-register'
import {
  buildAnswer,
  canSubmit,
  draftFor,
  sessionView,
  summaryView,
  type AnswerDraft,
  type SessionResult,
} from './review/session-view'
import { buildTodayPlan, openReviewWorkspace, type ReviewRuntime } from './review/workspace'
import './styles.css'

interface InstallPromptEvent extends Event {
  readonly platforms: readonly string[]
  prompt(): Promise<void>
  userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>
}

interface PwaState {
  needRefresh: boolean
  offlineReady: boolean
  installPrompt: InstallPromptEvent | null
  installDismissed: boolean
  deferred: boolean
}

const pwa: PwaState = {
  needRefresh: false,
  offlineReady: false,
  installPrompt: null,
  installDismissed: false,
  deferred: false,
}

let updateSW: ((reload?: boolean) => Promise<void>) | null = null

const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true

const maybeRoot = document.querySelector<HTMLDivElement>('#app')
if (maybeRoot === null) throw new Error('缺少 #app')
const root: HTMLDivElement = maybeRoot

const params = new URLSearchParams(location.search)
const apiOverride = params.get('api') ?? undefined
const localBase = params.get('base') ?? '/local-content/'
const localSource = new HttpContentSource(new URL('dist/content.json', new URL(localBase, location.href)).href)

const cache = new IndexedDbCache()

type Screen = 'setup' | 'loading' | 'data' | 'error' | 'session' | 'summary'
type Tab = 'today' | 'library' | 'progress' | 'settings'

interface SessionRun {
  readonly runtime: ReviewRuntime
  plan: Plan
  state: SessionState
  readonly startedAt: number
  readonly results: SessionResult[]
  readonly requeue: QueueItem[]
  readonly requeued: Set<string>
  draft: AnswerDraft
}

interface UiState {
  screen: Screen
  loading: boolean
  data: LoadedContent | null
  error: ContentError | null
  notice: string
  config: RepoConfig | null
  source: ContentSource | null
  draft: RepoDraft
  setupError: string | null
  local: boolean
  runtime: ReviewRuntime | null
  plan: Plan | null
  planError: string | null
  session: SessionRun | null
  tab: Tab
  reviewCount: number
  ratingCounts: Record<Rating, number>
  lastReviewedAt: number | null
}

const saved = params.get('source') === 'local' ? null : loadRepoConfig()
const initialDraft: RepoDraft = saved
  ? { url: saved.url, ref: saved.ref, bundlePath: saved.bundlePath }
  : { url: '', ref: DEFAULT_REF, bundlePath: DEFAULT_BUNDLE }

const state: UiState = {
  screen: params.get('source') === 'local' || saved !== null ? 'loading' : 'setup',
  loading: false,
  data: null,
  error: null,
  notice: '',
  config: saved,
  source: null,
  draft: initialDraft,
  setupError: null,
  local: params.get('source') === 'local',
  runtime: null,
  plan: null,
  planError: null,
  session: null,
  tab: 'today',
  reviewCount: 0,
  ratingCounts: { again: 0, hard: 0, good: 0, easy: 0 },
  lastReviewedAt: null,
}

const esc = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char] as string)

const formatTime = (ms: number): string =>
  new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(ms)

const shortSha = (sha: string): string => (sha.length > 18 ? `${sha.slice(0, 12)}…${sha.slice(-4)}` : sha)

const statusOf = (data: LoadedContent): { cls: string; text: string } => {
  if (data.offline) return { cls: 'offline', text: '离线缓存' }
  if (data.origin === 'remote') return { cls: 'live', text: '已更新' }
  return { cls: 'cached', text: '已是最新' }
}

const QUESTION_LABEL: Readonly<Record<string, string>> = {
  choice: '选择题',
  multiChoice: '多选题',
  trueFalse: '判断题',
  blank: '填空题',
  order: '排序题',
  match: '匹配题',
  freeRecall: '简答题',
  reading: '材料题',
}

function planSection(): string {
  if (state.planError !== null) {
    return `
      <section class="card plan-card">
        <div class="plan-head"><div><p class="eyebrow">今日计划</p><h2>队列生成失败</h2></div></div>
        <p class="form-error">${esc(state.planError)}</p>
      </section>`
  }

  const plan = state.plan
  if (plan === null) return ''

  const due = plan.items.filter((item) => item.reason === 'due').length
  const fresh = plan.items.filter((item) => item.reason === 'new').length
  const reviewedToday = state.runtime?.workspace.reviewedToday() ?? 0
  const rows = plan.items
    .slice(0, 6)
    .map((item) => {
      const type =
        item.subject.kind === 'concept'
          ? '知识点卡'
          : QUESTION_LABEL[item.subject.question.kind] ?? item.subject.question.kind
      const reason =
        item.reason === 'new'
          ? '新卡'
          : item.reason === 'due'
            ? item.overdue > 0
              ? `到期 ${item.overdue} 天`
              : '今天到期'
            : item.reason === 'sampled'
              ? '抽样'
              : '指定'
      return `
        <div class="plan-row">
          <span class="plan-type">${esc(type)}</span>
          <span class="plan-title">${esc(item.subject.concept.title)}</span>
          <span class="plan-reason">${esc(reason)}</span>
        </div>`
    })
    .join('')

  return `
    <section class="card plan-card">
      <div class="plan-head">
        <div>
          <p class="eyebrow">今日计划</p>
          <h2>今天有 ${plan.items.length} 张卡</h2>
        </div>
        <span class="plan-id">${esc(plan.planId)}</span>
      </div>

      <div class="plan-counts">
        <div><strong>${due}</strong><span>到期</span></div>
        <div><strong>${fresh}</strong><span>新卡</span></div>
        <div><strong>${plan.carryOver}</strong><span>积压</span></div>
        <div><strong>${reviewedToday}</strong><span>今日已复习</span></div>
      </div>

      ${
        plan.items.length === 0
          ? '<p class="plan-empty">今天没有到期卡。</p>'
          : `<div class="plan-list">${rows}${plan.items.length > 6 ? `<p class="plan-more">还有 ${plan.items.length - 6} 张，按队列顺序继续。</p>` : ''}</div>`
      }
      ${plan.notes.map((note) => `<p class="plan-note">${esc(note.detail)}</p>`).join('')}
      <div class="plan-actions">
        <button class="btn btn-primary" data-action="start-session" ${plan.items.length === 0 ? 'disabled' : ''}>开始复习</button>
        <span>${plan.items.length} 张 · ${due + fresh === plan.items.length ? '全部为本轮新卡/到期' : `到期 ${due} · 新卡 ${fresh}`}</span>
      </div>
      <div class="plan-data">
        <button class="btn btn-ghost btn-sm" data-action="export-reviews">导出复习记录</button>
        <button class="btn btn-ghost btn-sm" data-action="import-reviews">导入</button>
        <input id="review-import" type="file" accept=".jsonl,.json,text/plain" hidden />
      </div>
      <p class="plan-foot">队列来自 <code>workspace.plan({ kind: 'due', limit: 20 })</code>；提交会写入本地日志。</p>
    </section>`
}

function iosInstallHint(): string {
  if (!isIos || isStandalone()) return ''
  return '<p class="pwa-hint">iPhone / iPad：Safari → 分享 → 添加到主屏幕。</p>'
}

function renderPwaBanner(): void {
  const el = document.querySelector<HTMLDivElement>('#pwa-banner')
  if (el === null) return

  let message = ''
  let actions = ''

  if (pwa.needRefresh) {
    message = pwa.deferred ? '有新版本，本轮结束后更新' : '有新版本可用'
    actions = pwa.deferred
      ? '<button class="btn btn-quiet btn-sm" data-pwa="dismiss">知道了</button>'
      : '<button class="btn btn-primary btn-sm" data-pwa="update">更新</button>' +
        '<button class="btn btn-quiet btn-sm" data-pwa="dismiss">稍后</button>'
  } else if (pwa.offlineReady) {
    message = '已可离线打开'
    actions = '<button class="btn btn-quiet btn-sm" data-pwa="dismiss">知道了</button>'
  } else if (pwa.installPrompt !== null && !pwa.installDismissed && !isStandalone()) {
    message = '装到主屏，打开更快'
    actions =
      '<button class="btn btn-primary btn-sm" data-pwa="install">安装</button>' +
      '<button class="btn btn-quiet btn-sm" data-pwa="dismiss">稍后</button>'
  }

  el.hidden = message === ''
  el.innerHTML = message === '' ? '' : `<span class="pwa-msg">${esc(message)}</span>${actions}`
  document.body.classList.toggle('has-pwa-banner', message !== '')
}

function makeGiteeSource(config: RepoConfig): GiteeContentSource {
  return new GiteeContentSource({
    owner: config.owner,
    repo: config.repo,
    ref: config.ref,
    bundlePath: config.bundlePath,
    apiBase: apiOverride,
  })
}

let sessionTimer: ReturnType<typeof setInterval> | null = null

const clockText = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`
}

function stopSessionTimer(): void {
  if (sessionTimer !== null) {
    clearInterval(sessionTimer)
    sessionTimer = null
  }
}

function startSessionTimer(): void {
  stopSessionTimer()
  sessionTimer = setInterval(() => {
    const run = state.session
    const el = document.querySelector<HTMLElement>('#session-time')
    if (run === null || el === null) return
    el.textContent = clockText((Date.now() - run.startedAt) / 1000)
  }, 1000)
}

function sessionResult(item: QueueItem, next: SessionState): SessionResult | null {
  if (next.phase !== 'committed' || next.receipt.kind !== 'recorded') return null
  const result = next.receipt.result
  return {
    title: item.subject.concept.title,
    type:
      item.subject.kind === 'concept'
        ? '知识点卡'
        : QUESTION_LABEL[item.subject.question.kind] ?? item.subject.question.kind,
    rating: result.rating,
    score: result.outcome?.score ?? null,
    nextDueOn: result.nextDueOn,
    requeue: result.requeue,
  }
}

function rememberResult(run: SessionRun, item: QueueItem, next: SessionState): void {
  const result = sessionResult(item, next)
  if (result === null) return
  run.results.push(result)
  if (result.requeue) {
    const key = String(cardOf(item))
    if (!run.requeued.has(key)) {
      run.requeued.add(key)
      run.requeue.push(item)
    }
  }
}

async function startSession(planOverride?: Plan): Promise<void> {
  const runtime = state.runtime
  if (runtime === null) return

  const plan = planOverride ?? buildTodayPlan(runtime)
  if (planOverride === undefined) state.plan = plan
  if (plan.items.length === 0) {
    state.screen = 'data'
    state.tab = 'today'
    state.notice = '这一轮没有可复习的卡。'
    render()
    return
  }

  try {
    const initial = await runtime.workspace.stepSession(IDLE, { t: 'start', plan })
    state.session = {
      runtime,
      plan,
      state: initial,
      startedAt: Date.now(),
      results: [],
      requeue: [],
      requeued: new Set(),
      draft: draftFor(currentOf(initial)),
    }
    state.screen = initial.phase === 'ended' ? 'summary' : 'session'
    if (initial.phase === 'ended') stopSessionTimer()
    else startSessionTimer()
    pwa.deferred = pwa.needRefresh
    renderPwaBanner()
    render()
  } catch (error) {
    state.notice = `无法开始本轮：${error instanceof Error ? error.message : String(error)}`
    state.screen = 'data'
    render()
  }
}

async function startCardSession(index: number): Promise<void> {
  const runtime = state.runtime
  const item = state.plan?.items[index]
  if (runtime === null || item === undefined) return

  const plan = runtime.workspace.plan({ kind: 'browse', cards: [cardOf(item)] })
  if (plan.items.length === 0) {
    state.notice = '这张卡已经不在当前内容里，刷新后再试。'
    render()
    return
  }
  await startSession(plan)
}

function updateSessionAction(): void {
  const run = state.session
  if (run === null || run.state.phase !== 'answering') return
  const item = currentOf(run.state)
  const button = document.querySelector<HTMLButtonElement>('#session-action')
  if (item === null || button === null) return
  if (itemScoring(item) === 'self') return
  button.disabled = !canSubmit(item, run.draft)
}

async function submitAuto(): Promise<void> {
  const run = state.session
  if (run === null || run.state.phase !== 'answering') return
  const item = currentOf(run.state)
  if (item === null || item.subject.kind === 'concept' || itemScoring(item) !== 'auto') return
  if (!canSubmit(item, run.draft)) return

  try {
    let next = await run.runtime.workspace.stepSession(run.state, {
      t: 'respond',
      answer: buildAnswer(item, run.draft),
    })
    if (next.phase === 'revealed') {
      const committed = await run.runtime.workspace.stepSession(next, { t: 'commit' })
      rememberResult(run, item, committed)
      next = committed
    }
    run.state = next
    render()
  } catch (error) {
    state.notice = `提交失败：${error instanceof Error ? error.message : String(error)}`
    render()
  }
}

async function revealSelf(): Promise<void> {
  const run = state.session
  if (run === null || run.state.phase !== 'answering') return
  const item = currentOf(run.state)
  if (item === null || itemScoring(item) !== 'self') return
  try {
    run.state = await run.runtime.workspace.stepSession(run.state, { t: 'reveal' })
    render()
  } catch (error) {
    state.notice = `无法显示参考答案：${error instanceof Error ? error.message : String(error)}`
    render()
  }
}

async function rateSelf(rating: Rating): Promise<void> {
  const run = state.session
  if (run === null || run.state.phase !== 'reporting') return
  const item = currentOf(run.state)
  if (item === null) return

  try {
    let next = await run.runtime.workspace.stepSession(run.state, { t: 'rate', rating })
    if (next.phase === 'revealed') {
      const committed = await run.runtime.workspace.stepSession(next, { t: 'commit' })
      rememberResult(run, item, committed)
      next = committed
    }
    run.state = next
    render()
  } catch (error) {
    state.notice = `记录失败：${error instanceof Error ? error.message : String(error)}`
    render()
  }
}

async function advanceSession(): Promise<void> {
  const run = state.session
  if (run === null) return

  try {
    let next = run.state
    if (next.phase === 'revealed') {
      const item = currentOf(next)
      const committed = await run.runtime.workspace.stepSession(next, { t: 'commit' })
      if (item !== null) rememberResult(run, item, committed)
      next = committed
    }
    if (next.phase === 'committed') next = await run.runtime.workspace.stepSession(next, { t: 'next' })
    run.state = next

    if (next.phase === 'ended') {
      if (run.requeue.length > 0) {
        const extraPlan = run.runtime.workspace.plan({
          kind: 'browse',
          cards: run.requeue.map((item) => cardOf(item)),
        })
        const extra = await run.runtime.workspace.stepSession(IDLE, { t: 'start', plan: extraPlan })
        if (extra.phase === 'answering') {
          run.plan = extraPlan
          run.state = extra
          run.requeue.length = 0
          run.draft = draftFor(currentOf(extra))
          state.screen = 'session'
          render()
          return
        }
      }
      stopSessionTimer()
      state.screen = 'summary'
    } else {
      run.draft = draftFor(currentOf(next))
      state.screen = 'session'
    }
    render()
  } catch (error) {
    state.notice = `无法进入下一题：${error instanceof Error ? error.message : String(error)}`
    render()
  }
}

async function exitSession(): Promise<void> {
  const run = state.session
  if (run !== null && run.state.phase !== 'ended') {
    try {
      await run.runtime.workspace.stepSession(run.state, { t: 'abandon' })
    } catch {
      // 已经离开，不再阻塞。
    }
  }
  stopSessionTimer()
  state.session = null
  state.screen = 'data'
  state.tab = 'today'
  state.notice = '已退出本轮；未提交的答案不会记录。'
  if (state.runtime !== null) state.plan = buildTodayPlan(state.runtime)
  pwa.deferred = false
  renderPwaBanner()
  render()
  if (state.runtime !== null) void refreshLogSummary(state.runtime).then(() => { if (state.screen === 'data') render() })
}

function backToToday(): void {
  stopSessionTimer()
  const hadResults = state.session !== null && state.session.results.length > 0
  state.session = null
  state.screen = 'data'
  state.tab = 'today'
  state.notice = hadResults ? '本轮已写入本地日志。' : '本轮没有记录。'
  if (state.runtime !== null) state.plan = buildTodayPlan(state.runtime)
  pwa.deferred = false
  renderPwaBanner()
  render()
  if (state.runtime !== null) void refreshLogSummary(state.runtime).then(() => { if (state.screen === 'data') render() })
}

async function restartSession(): Promise<void> {
  const runtime = state.runtime
  stopSessionTimer()
  state.session = null
  if (runtime === null) {
    state.screen = 'data'
    render()
    return
  }
  state.plan = buildTodayPlan(runtime)
  await startSession()
}

function updateDraftFromInput(target: HTMLElement): void {
  const run = state.session
  if (run === null || run.state.phase !== 'answering') return
  const input = target as HTMLInputElement | HTMLTextAreaElement
  const kind = input.dataset.sessionInput
  if (kind === 'recall') {
    run.draft.text = input.value
  } else if (kind === 'reading-text') {
    const index = Number(input.dataset.index)
    if (Number.isInteger(index) && index >= 0) run.draft.readingText[index] = input.value
  } else if (kind === 'reading-blank') {
    const index = Number(input.dataset.index)
    const blankIndex = Number(input.dataset.blankIndex)
    if (Number.isInteger(index) && index >= 0 && Number.isInteger(blankIndex) && blankIndex >= 0) {
      const values = run.draft.readingBlanks[index] ?? []
      values[blankIndex] = input.value
      run.draft.readingBlanks[index] = values
    }
  } else if (kind === 'blank') {
    const index = Number(input.dataset.index)
    if (Number.isInteger(index) && index >= 0) run.draft.blanks[index] = input.value
  }
  updateSessionAction()
}

function handleSessionControl(target: HTMLElement): void {
  const action = target.dataset.session
  if (action === 'rate') {
    void rateSelf(target.dataset.value as Rating)
    return
  }

  // 只有真正的控件才进入会话事件；普通容器（例如旧版 blank-wrap）不能触发 render，
  // 否则点击输入框后 DOM 被替换，焦点会立刻丢失，手机上看起来就是“输入不进去”。
  const controls = new Set([
    'choice',
    'reading-choice',
    'reading-tf',
    'multi',
    'tf',
    'order',
    'match-left',
    'match-right',
  ])
  if (!controls.has(action ?? '')) return

  const run = state.session
  if (run === null || run.state.phase !== 'answering') return
  const value = Number(target.dataset.value)

  if (action === 'choice') run.draft.choice = value
  else if (action === 'reading-choice') {
    const index = Number(target.dataset.index)
    if (Number.isInteger(index) && index >= 0) run.draft.readingChoice[index] = value
  } else if (action === 'reading-tf') {
    const index = Number(target.dataset.index)
    if (Number.isInteger(index) && index >= 0) run.draft.readingTf[index] = target.dataset.value === 'true'
  } else if (action === 'multi') {
    run.draft.multi = run.draft.multi.includes(value)
      ? run.draft.multi.filter((item) => item !== value)
      : [...run.draft.multi, value]
  } else if (action === 'tf') run.draft.tf = target.dataset.value === 'true'
  else if (action === 'order') {
    run.draft.order = run.draft.order.includes(value)
      ? run.draft.order.filter((item) => item !== value)
      : [...run.draft.order, value]
  } else if (action === 'match-left') {
    if (run.draft.match[value] !== undefined) delete run.draft.match[value]
    run.draft.matchSel =
      run.draft.matchSel?.side === 'left' && run.draft.matchSel.index === value
        ? null
        : { side: 'left', index: value }
  } else if (action === 'match-right') {
    const left = Object.keys(run.draft.match).find((key) => run.draft.match[Number(key)] === value)
    if (left !== undefined) delete run.draft.match[Number(left)]
    if (run.draft.matchSel?.side === 'left') {
      run.draft.match[run.draft.matchSel.index] = value
      run.draft.matchSel = null
    } else {
      run.draft.matchSel =
        run.draft.matchSel?.side === 'right' && run.draft.matchSel.index === value
          ? null
          : { side: 'right', index: value }
    }
  }
  render()
}

function handleSessionAction(action: string): void {
  if (action === 'session-submit') void submitAuto()
  else if (action === 'session-reveal') void revealSelf()
  else if (action === 'session-next') void advanceSession()
}

function sessionScreen(): string {
  if (state.session === null) return ''
  return sessionView({
    plan: state.session.plan,
    state: state.session.state,
    startedAt: state.session.startedAt,
    results: state.session.results,
    draft: state.session.draft,
  })
}

function summaryScreen(): string {
  if (state.session === null) return ''
  return summaryView(
    {
      plan: state.session.plan,
      state: state.session.state,
      startedAt: state.session.startedAt,
      results: state.session.results,
      draft: state.session.draft,
    },
    (Date.now() - state.session.startedAt) / 1000,
  )
}

function header(): string {
  const label = state.source?.label ?? (state.config?.url ?? '未连接')
  return `
    <header class="topbar">
      <div class="brand">
        <span class="seal">关</span>
        <div class="brand-copy"><strong>关键概念</strong><small>LOCAL REVIEW</small></div>
      </div>
      <span class="source-pill">${esc(label)}</span>
    </header>`
}

function setupView(): string {
  const draft = state.draft
  return `
    ${header()}
    <main class="page">
      <p class="eyebrow">首次连接</p>
      <h1>连接知识仓库</h1>
      <p class="lede">填写公开的 Gitee 内容仓。首次连接成功后，地址会保存在本机浏览器；答题记录只存在本地，不会上传。</p>

      <form class="card setup-card" id="repo-form" novalidate>
        <label class="field">
          <span>仓库地址</span>
          <input name="url" value="${esc(draft.url)}" placeholder="https://gitee.com/owner/repo" autocomplete="off" spellcheck="false" inputmode="url" />
        </label>
        <p class="field-hint">支持 https://gitee.com/owner/repo、git@gitee.com:owner/repo.git 或 owner/repo。</p>

        <div class="field-row">
          <label class="field">
            <span>分支</span>
            <input name="ref" value="${esc(draft.ref)}" autocomplete="off" spellcheck="false" />
          </label>
          <label class="field">
            <span>内容包路径</span>
            <input name="bundlePath" value="${esc(draft.bundlePath)}" autocomplete="off" spellcheck="false" />
          </label>
        </div>

        ${state.setupError === null ? '' : `<p class="form-error">${esc(state.setupError)}</p>`}
        ${state.config === null ? '' : `<p class="saved-hint">当前保存：<code>${esc(state.config.url)}</code></p>`}

        <div class="actions">
          <button class="btn btn-primary" type="submit" ${state.loading ? 'disabled' : ''}>${state.loading ? '正在连接…' : '连接并保存'}</button>
          ${state.data === null ? '' : '<button class="btn btn-ghost" type="button" data-action="cancel">取消</button>'}
          ${import.meta.env.DEV ? '<a class="btn btn-quiet" href="?source=local">用本地内容测试</a>' : ''}
        </div>
      </form>

      <p class="foot">内容仓需要是公开仓库；当前版本不支持私有仓库 token。地址保存在浏览器 localStorage 的 <code>${REPO_STORAGE_KEY}</code>。</p>
      ${iosInstallHint()}
    </main>`
}

function loadingView(): string {
  return `
    ${header()}
    <main class="page">
      <p class="eyebrow">内容源</p>
      <h1>内容同步</h1>
      <p class="lede">从只读内容仓读取 <code>dist/content.json</code>，交给 <code>@kc/core</code> 解析。</p>
      <section class="card loading-card" aria-busy="true">
        <span class="spinner" aria-hidden="true"></span>
        <div><strong>${state.notice || '正在检查内容版本…'}</strong><p>${esc(state.source?.key ?? '正在准备内容源')}</p></div>
      </section>
    </main>`
}

function errorView(error: ContentError): string {
  const hints: string[] = []
  if (error.code === 'not-found') {
    hints.push('确认仓库是公开的，分支名和内容包路径正确。')
    hints.push('确认已经在内容仓运行 npm run build，并 push 了 dist/content.json。')
  } else if (error.code === 'rate-limit') {
    hints.push('Gitee 匿名 API 有频率限制，等一分钟再试。')
    hints.push('不要连续点“强制刷新”；正常加载只会在内容变化时下载。')
  } else if (error.code === 'invalid' || error.code === 'parse') {
    hints.push('检查 content.json 是否是构建产物，不要手改。')
    hints.push('检查 Markdown 是否为 UTF-8、题块是否能通过 npm run check。')
  } else {
    hints.push('检查网络连接；手机端从后台恢复时尤其容易遇到超时。')
    hints.push('如果之前成功加载过，重新打开页面会优先使用本地缓存。')
  }

  return `
    ${header()}
    <main class="page">
      <p class="eyebrow">内容源</p>
      <h1>加载失败</h1>
      <p class="lede">${state.config === null ? '还没有可用的仓库配置。' : `已保存的仓库：<code>${esc(state.config.url)}</code>。`}缓存为空时，只能停在错误页。</p>

      <section class="card error-card">
        <div class="error-head">
          <span class="error-code">${esc(error.code)}</span>
          ${error.status === undefined ? '' : `<span class="error-status">HTTP ${error.status}</span>`}
        </div>
        <pre>${esc(error.message)}</pre>
        <ul class="hints">${hints.map((hint) => `<li>${esc(hint)}</li>`).join('')}</ul>
        <div class="actions">
          <button class="btn btn-primary" data-action="refresh" ${state.loading ? 'disabled' : ''}>重试</button>
          <button class="btn btn-ghost" data-action="change-repo" ${state.loading ? 'disabled' : ''}>切换仓库</button>
          ${import.meta.env.DEV && !state.local ? '<a class="btn btn-quiet" href="?source=local">用本地内容测试</a>' : ''}
        </div>
      </section>

      <p class="foot">${esc(state.source?.key ?? state.config?.url ?? '未连接')}</p>
    </main>`
}

const formatDay = (): string =>
  new Intl.DateTimeFormat('zh-CN', { month: 'long', day: 'numeric', weekday: 'long' }).format(new Date())

const TABS: readonly { id: Tab; label: string; glyph: string }[] = [
  { id: 'today', label: '今天', glyph: '今' },
  { id: 'library', label: '概念库', glyph: '库' },
  { id: 'progress', label: '进度', glyph: '进' },
  { id: 'settings', label: '设置', glyph: '设' },
]

function tabBar(): string {
  return `<nav class="tabbar" aria-label="主导航">${TABS.map(
    (tab) => `<button class="tab ${state.tab === tab.id ? 'is-active' : ''}" data-action="tab" data-tab="${tab.id}">
      <span class="tab-glyph" aria-hidden="true">${tab.glyph}</span>
      <span class="tab-label">${tab.label}</span>
    </button>`,
  ).join('')}</nav>`
}

function todayType(item: QueueItem): string {
  return item.subject.kind === 'concept'
    ? '知识点卡'
    : QUESTION_LABEL[item.subject.question.kind] ?? item.subject.question.kind
}

function todayReason(item: QueueItem): string {
  if (item.reason === 'new') return '新卡'
  if (item.reason === 'due') return item.overdue > 0 ? `逾期 ${item.overdue} 天` : '今天到期'
  if (item.reason === 'sampled') return '抽样'
  return '指定'
}

function todayView(data: LoadedContent): string {
  const plan = state.plan
  const due = plan?.items.filter((item) => item.reason === 'due').length ?? 0
  const fresh = plan?.items.filter((item) => item.reason === 'new').length ?? 0
  const reviewedToday = state.runtime?.workspace.reviewedToday() ?? 0
  const warning =
    data.offline && data.error
      ? `<div class="banner">网络失败，正在显示 ${esc(formatTime(data.fetchedAt))} 的缓存。<span>${esc(data.error.message)}</span></div>`
      : ''
  const queue =
    plan === null
      ? ''
      : plan.items
          .map(
            (item, index) => `<button type="button" class="today-row" data-action="review-card" data-index="${index}"
              title="直接复习这张卡" aria-label="开始复习 ${esc(item.subject.concept.title)} · ${esc(todayType(item))}">
              <span class="today-row-type">${esc(todayType(item))}</span>
              <span class="today-row-title">${esc(item.subject.concept.title)}</span>
              <span class="today-row-reason">${esc(todayReason(item))}</span>
              <span class="today-row-go" aria-hidden="true">→</span>
            </button>`,
          )
          .join('')

  const hero =
    plan === null
      ? `<section class="today-hero is-empty"><div><p class="eyebrow">今日队列</p><h2>队列还没有准备好</h2><p>${esc(state.planError ?? '正在生成今日计划…')}</p></div></section>`
      : `<section class="today-hero ${plan.items.length === 0 ? 'is-empty' : ''}">
          <div class="today-hero-main">
            <p class="eyebrow">今日队列</p>
            <div class="today-count"><strong>${plan.items.length}</strong><span>张待复习</span></div>
            <p>${plan.items.length === 0 ? '今天没有到期卡，去概念库看看，或明天再来。' : '按计划顺序完成；提交后会写入本地日志。'}</p>
          </div>
          <div class="today-hero-side">
            <div class="today-mini"><strong>${due}</strong><span>到期</span></div>
            <div class="today-mini"><strong>${fresh}</strong><span>新卡</span></div>
            <div class="today-mini"><strong>${reviewedToday}</strong><span>已复习</span></div>
            <button class="btn btn-primary today-start" data-action="start-session" ${plan.items.length === 0 ? 'disabled' : ''}>开始复习</button>
          </div>
        </section>`

  return `
    ${header()}
    <main class="page today-page">
      <p class="eyebrow">${formatDay()}</p>
      <h1>今天</h1>
      <p class="lede">${data.library.conceptCount} 个概念、${data.library.cardCount} 张卡；先做今天到期的，不追求清空。</p>
      ${warning}
      ${hero}
      ${
        plan !== null && plan.items.length > 0
          ? `<section class="card today-queue">
              <div class="section-head"><h2>这一轮</h2><span>${plan.carryOver > 0 ? `点一条直接开始 · 另有 ${plan.carryOver} 张积压` : '点一条直接开始'}</span></div>
              ${queue}
              ${plan.notes.map((note) => `<p class="plan-note">${esc(note.detail)}</p>`).join('')}
            </section>`
          : ''
      }
      <section class="card today-tip">
        <div><p class="eyebrow">怎么用</p><h2>碎片时间也能推进</h2></div>
        <p>一轮 10 张，大约 3–5 分钟。答错会按间隔重排；不用一次学完。</p>
      </section>
      <p class="foot">${esc(state.notice || '内容来自公开 Gitee 仓，复习记录只保存在本机。')}</p>
      ${iosInstallHint()}
    </main>
    ${tabBar()}`
}

function libraryView(data: LoadedContent): string {
  return `
    ${header()}
    <main class="page">
      <p class="eyebrow">内容库</p>
      <h1>概念库</h1>
      <p class="lede">${data.library.conceptCount} 个概念，${data.library.questionCount} 道题。点开正文与排期详情在 v0.2 接入。</p>
      <section class="concepts">
        <div class="section-head"><h2>全部概念</h2><span>${data.library.concepts.length} / ${data.library.conceptCount}</span></div>
        <div class="concept-list">${data.library.concepts
          .map(
            (concept) => `<article class="concept">
              <div class="concept-head"><h3>${esc(concept.title)}</h3><span class="difficulty">难度 ${concept.difficulty}</span></div>
              <p class="concept-meta">${concept.tags.map((tag) => `<span class="tag">${esc(tag)}</span>`).join('')}<span class="dot"></span>${concept.questions.length} 道题<span class="dot"></span>${concept.sections.length} 个段落</p>
            </article>`,
          )
          .join('')}</div>
      </section>
      <p class="foot">${esc(state.notice || '只读内容包 · 本地复习记录不随内容包变化。')}</p>
    </main>
    ${tabBar()}`
}

function progressView(data: LoadedContent): string {
  const total = state.reviewCount
  const ratings = state.ratingCounts
  const max = Math.max(1, ...Object.values(ratings))
  return `
    ${header()}
    <main class="page">
      <p class="eyebrow">学习进度</p>
      <h1>进度</h1>
      <p class="lede">数字来自本机复习日志；清内容缓存不会删日志。</p>
      <div class="stats">
        <div class="stat"><span>累计记录</span><strong>${total}</strong><small>条</small></div>
        <div class="stat"><span>今日已复习</span><strong>${state.runtime?.workspace.reviewedToday() ?? 0}</strong><small>张</small></div>
        <div class="stat"><span>今日队列</span><strong>${state.plan?.items.length ?? 0}</strong><small>张</small></div>
        <div class="stat"><span>概念</span><strong>${data.library.conceptCount}</strong><small>个</small></div>
      </div>
      <section class="card progress-card">
        <div class="section-head"><h2>评分分布</h2><span>${total} 条</span></div>
        ${(['again', 'hard', 'good', 'easy'] as const)
          .map(
            (rating) => `<div class="rating-bar-row"><span>${rating === 'again' ? '重来' : rating === 'hard' ? '困难' : rating === 'good' ? '良好' : '简单'}</span><div class="rating-bar"><i style="width:${Math.round((ratings[rating] / max) * 100)}%"></i></div><strong>${ratings[rating]}</strong></div>`,
          )
          .join('')}
        <p class="progress-foot">${state.lastReviewedAt === null ? '还没有复习记录。' : `最近一次：${esc(formatTime(state.lastReviewedAt))}`}</p>
      </section>
      <p class="foot">导出/导入在设置页。</p>
    </main>
    ${tabBar()}`
}

function settingsView(data: LoadedContent): string {
  const status = statusOf(data)
  const config = state.config
  const warning =
    data.offline && data.error
      ? `<div class="banner">网络失败，正在显示 ${esc(formatTime(data.fetchedAt))} 的缓存。<span>${esc(data.error.message)}</span></div>`
      : ''

  return `
    ${header()}
    <main class="page">
      <p class="eyebrow">设置</p>
      <h1>设置</h1>
      <p class="lede">内容源、缓存与本地复习记录。内容只读，日志只在本机。</p>

      ${warning}

      <section class="card source-card">
        <div class="card-head">
          <div>
            <h2>${esc(state.source?.label ?? config?.url ?? '内容源')}</h2>
            <p>${state.local ? 'HTTP · dist/content.json' : 'Gitee API · tree → blob'}</p>
          </div>
          <span class="status ${status.cls}"><i></i>${status.text}</span>
        </div>

        <p class="repo-url">
          ${config === null ? '本地内容 · 不保存仓库地址' : `${esc(config.url)} · 已保存到本机浏览器`}
        </p>

        <div class="actions">
          <button class="btn btn-primary" data-action="refresh" ${state.loading ? 'disabled' : ''}>检查更新</button>
          <button class="btn btn-ghost" data-action="force" ${state.loading ? 'disabled' : ''}>强制刷新</button>
          <button class="btn btn-quiet" data-action="clear" ${state.loading ? 'disabled' : ''}>清除内容缓存</button>
          <button class="btn btn-quiet" data-action="change-repo" ${state.loading ? 'disabled' : ''}>${state.local ? '连接 Gitee 仓库' : '切换仓库'}</button>
        </div>

        <p class="notice">${esc(state.notice)}</p>

        <details class="debug">
          <summary>调试信息</summary>
          <dl>
            <dt>来源</dt><dd><code>${esc(state.source?.key ?? '')}</code></dd>
            ${config === null ? '' : `<dt>保存于</dt><dd>${esc(formatTime(config.savedAt))}</dd>`}
            <dt>远端 blob</dt><dd><code>${esc(shortSha(data.sha))}</code></dd>
            <dt>内容哈希</dt><dd><code>${esc(data.bundle.contentHash)}</code></dd>
            <dt>拉取时间</dt><dd>${esc(formatTime(data.fetchedAt))}</dd>
            <dt>检查时间</dt><dd>${esc(formatTime(data.checkedAt))}</dd>
            <dt>文件数</dt><dd>${data.files.length} 个 Markdown</dd>
          </dl>
        </details>
      </section>

      <section class="card data-card">
        <div class="card-head">
          <div><h2>复习记录</h2><p>IndexedDB · kc-review/reviews</p></div>
          <span class="data-count">${state.reviewCount} 条</span>
        </div>
        <p class="section-lede">导出后可以备份到文件；导入按 id 去重，不会覆盖已有记录。</p>
        <div class="actions">
          <button class="btn btn-ghost" data-action="export-reviews">导出 JSONL</button>
          <button class="btn btn-ghost" data-action="import-reviews">导入 JSONL</button>
          <input id="review-import" type="file" accept=".jsonl,.json,text/plain" hidden />
        </div>
      </section>

      <section class="card diagnostics-card">
        <div class="card-head"><div><h2>PWA 状态</h2><p>装到主屏后回到这里核对</p></div></div>
        <dl class="diag">
          <dt>安全上下文</dt><dd class="${window.isSecureContext ? 'ok' : 'warn'}">${window.isSecureContext ? '是' : '否'}</dd>
          <dt>Service Worker</dt><dd class="${navigator.serviceWorker.controller !== null ? 'ok' : 'warn'}">${navigator.serviceWorker.controller !== null ? '已控制' : '等待注册'}</dd>
          <dt>显示模式</dt><dd class="${isStandalone() ? 'ok' : 'warn'}">${isStandalone() ? 'standalone' : '浏览器标签'}</dd>
          <dt>安装提示</dt><dd>${pwa.installPrompt !== null ? '可用' : '未触发'}</dd>
          <dt>Manifest</dt><dd><code>${esc(document.querySelector<HTMLLinkElement>('link[rel=manifest]')?.href ?? '未注入')}</code></dd>
        </dl>
        <p class="section-lede">手机上三项为「是 / 已控制 / standalone」即安装成功；只有 localhost 或 HTTPS 才会注册 SW。</p>
      </section>

      <div class="stats">
        <div class="stat"><span>概念</span><strong>${data.library.conceptCount}</strong><small>个</small></div>
        <div class="stat"><span>题目</span><strong>${data.library.questionCount}</strong><small>道</small></div>
        <div class="stat"><span>卡片</span><strong>${data.library.cardCount}</strong><small>张</small></div>
        <div class="stat"><span>资产</span><strong>${data.bundle.assets.length}</strong><small>个</small></div>
      </div>

      <p class="foot">内容版本变化不影响本地日志；清除内容缓存只删内容包。</p>
      ${iosInstallHint()}
    </main>
    ${tabBar()}`
}

function render(): void {
  document.body.classList.toggle('has-tabbar', state.screen === 'data')
  if (state.screen === 'setup') {
    root.innerHTML = setupView()
    return
  }
  if (state.screen === 'loading') {
    root.innerHTML = loadingView()
    return
  }
  if (state.screen === 'error') {
    root.innerHTML = errorView(state.error ?? asContentError(new Error('未知错误')))
    return
  }
  if (state.screen === 'session' && state.session !== null) {
    root.innerHTML = sessionScreen()
    return
  }
  if (state.screen === 'summary' && state.session !== null) {
    root.innerHTML = summaryScreen()
    return
  }
  if (state.data !== null) {
    if (state.tab === 'library') root.innerHTML = libraryView(state.data)
    else if (state.tab === 'progress') root.innerHTML = progressView(state.data)
    else if (state.tab === 'settings') root.innerHTML = settingsView(state.data)
    else root.innerHTML = todayView(state.data)
    return
  }
  root.innerHTML = setupView()
}

async function refreshLogSummary(runtime: ReviewRuntime): Promise<void> {
  const { reviews } = await runtime.reviews.readAll()
  const counts: Record<Rating, number> = { again: 0, hard: 0, good: 0, easy: 0 }
  for (const review of reviews) counts[review.rating] += 1
  state.reviewCount = reviews.length
  state.ratingCounts = counts
  state.lastReviewedAt = reviews.at(-1)?.reviewedAtMs ?? null
}

async function attachPlan(data: LoadedContent): Promise<void> {
  state.runtime = null
  state.plan = null
  state.planError = null

  try {
    const runtime = await openReviewWorkspace(data.files)
    state.runtime = runtime
    state.plan = buildTodayPlan(runtime)
    await refreshLogSummary(runtime)
  } catch (error) {
    state.planError = error instanceof Error ? error.message : String(error)
  }
}

async function refresh(force: boolean): Promise<void> {
  if (state.source === null) {
    state.screen = 'setup'
    render()
    return
  }

  state.loading = true
  state.notice = force ? '正在强制拉取最新内容…' : '正在检查内容版本…'
  if (state.data === null) state.screen = 'loading'
  render()

  try {
    const data = await loadContent(state.source, cache, { force })
    state.data = data
    state.error = data.error ?? null
    await attachPlan(data)
    state.screen = 'data'
    state.notice = data.offline
      ? '远端不可用，已回退到本地缓存。'
      : data.origin === 'remote'
        ? `内容已更新 · ${formatTime(data.checkedAt)}`
        : `内容没有变化 · ${formatTime(data.checkedAt)}`
  } catch (error) {
    state.error = asContentError(error)
    state.screen = 'error'
    state.notice = '加载失败，详情见下方。'
  } finally {
    state.loading = false
    render()
  }
}

async function connect(draft: RepoDraft): Promise<void> {
  const resolved = resolveRepoConfig(draft)
  if (!resolved.ok) {
    state.draft = draft
    state.setupError = resolved.error
    state.screen = 'setup'
    state.loading = false
    render()
    return
  }

  const candidate = makeGiteeSource(resolved.value)
  state.loading = true
  state.setupError = null
  state.notice = '正在连接内容仓…'
  state.screen = 'loading'
  render()

  try {
    const data = await loadContent(candidate, cache, { force: false })
    const savedConfig = saveRepoConfig(resolved.value)
    state.source = candidate
    state.config = savedConfig
    state.data = data
    state.error = data.error ?? null
    state.local = false
    await attachPlan(data)
    state.screen = 'data'
    state.tab = 'today'
    state.draft = { url: savedConfig.url, ref: savedConfig.ref, bundlePath: savedConfig.bundlePath }
    state.notice = data.offline
      ? '远端不可用，已回退到本地缓存。地址已保存。'
      : `已连接并保存地址 · ${formatTime(savedConfig.savedAt)}`
  } catch (error) {
    state.setupError = asContentError(error).message
    state.screen = 'setup'
    state.notice = ''
  } finally {
    state.loading = false
    render()
  }
}

function openSetup(): void {
  if (state.local) {
    state.local = false
    state.source = null
    state.data = null
    state.error = null
    state.plan = null
    state.runtime = null
  }
  state.draft = state.config
    ? { url: state.config.url, ref: state.config.ref, bundlePath: state.config.bundlePath }
    : state.draft
  state.setupError = null
  state.screen = 'setup'
  render()
}

function cancelSetup(): void {
  state.setupError = null
  if (state.data !== null) {
    state.screen = 'data'
    render()
    return
  }
  if (state.source !== null) {
    state.screen = 'loading'
    void refresh(false)
    return
  }
  state.screen = 'setup'
  render()
}

async function clearCache(): Promise<void> {
  await cache.clear()
  if (state.source === null) {
    state.screen = 'setup'
    render()
    return
  }
  state.data = null
  state.notice = '本地缓存已清除。'
  await refresh(true)
}

function todayStamp(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

async function exportReviews(): Promise<void> {
  const runtime = state.runtime
  if (runtime === null) return
  const jsonl = await runtime.reviews.exportJsonl()
  const count = (await runtime.reviews.readAll()).reviews.length
  if (count === 0) {
    state.notice = '还没有复习记录可导出。'
    render()
    return
  }

  const blob = new Blob([jsonl], { type: 'application/x-ndjson;charset=utf-8' })
  const name = `kc-reviews-${todayStamp()}.jsonl`
  const file = new File([blob], name, { type: 'application/x-ndjson' })
  if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: '关键概念复习记录' })
      state.notice = `已打开系统分享（${count} 条）。`
      render()
      return
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      // 分享失败时继续走下载。
    }
  }

  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 2000)
  state.notice = `已导出 ${count} 条复习记录。`
  render()
}

async function importReviews(file: File): Promise<void> {
  const runtime = state.runtime
  if (runtime === null) return
  const result = await runtime.reviews.importJsonl(await file.text())
  await runtime.workspace.reload()
  state.plan = buildTodayPlan(runtime)
  await refreshLogSummary(runtime)
  state.notice = `导入 ${result.imported} 条；跳过重复 ${result.skipped} 条；无效 ${result.invalid} 条。`
  render()
}

root.addEventListener('submit', (event) => {
  const form = (event.target as HTMLElement | null)?.closest<HTMLFormElement>('#repo-form')
  if (form === null) return
  event.preventDefault()

  const data = new FormData(form)
  const draft: RepoDraft = {
    url: String(data.get('url') ?? ''),
    ref: String(data.get('ref') ?? ''),
    bundlePath: String(data.get('bundlePath') ?? ''),
  }
  state.draft = draft
  void connect(draft)
})

root.addEventListener('input', (event) => {
  const target = event.target as HTMLElement | null
  if (target?.matches('[data-session-input]')) updateDraftFromInput(target)
})

root.addEventListener('change', (event) => {
  const input = event.target as HTMLInputElement | null
  if (input?.id !== 'review-import') return
  const file = input.files?.[0]
  if (file !== undefined) void importReviews(file).catch((error: unknown) => {
    state.notice = `导入失败：${error instanceof Error ? error.message : String(error)}`
    render()
  })
  input.value = ''
})

root.addEventListener('pointerdown', (event) => {
  const field = (event.target as HTMLElement | null)?.closest<HTMLInputElement | HTMLTextAreaElement>(
    'input, textarea',
  )
  if (field !== null && field !== undefined && !field.disabled && !field.readOnly) {
    // 某些桌面环境下 input 的默认点击聚焦会被扩展/浏览器设置吞掉；
    // 在捕获阶段直接聚焦，Tab 与鼠标两条路径都走同一个输入框。
    field.focus()
  }
}, true)

root.addEventListener('click', (event) => {
  // 输入框自己的点击不能被会话控件/根节点处理接管，
  // 否则 render() 会替换 DOM，鼠标焦点刚落下就丢失。
  const eventTarget = event.target as HTMLElement | null
  if (eventTarget?.closest('input, textarea, select')) return

  const sessionControl = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-session]')
  if (sessionControl) {
    handleSessionControl(sessionControl)
    return
  }

  const sessionAction = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-session-action]')
  if (sessionAction) {
    handleSessionAction(sessionAction.dataset.sessionAction ?? '')
    return
  }

  const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]')
  if (!target) return

  const action = target.dataset.action
  if (action === 'refresh') void refresh(false)
  else if (action === 'force') void refresh(true)
  else if (action === 'clear') void clearCache()
  else if (action === 'change-repo') openSetup()
  else if (action === 'cancel') cancelSetup()
  else if (action === 'start-session') void startSession()
  else if (action === 'review-card') {
    const index = Number(target.dataset.index)
    if (Number.isInteger(index) && index >= 0) void startCardSession(index)
  }
  else if (action === 'exit-session') void exitSession()
  else if (action === 'summary-back') backToToday()
  else if (action === 'summary-restart') void restartSession()
  else if (action === 'tab') {
    const tab = target.dataset.tab
    if (tab === 'today' || tab === 'library' || tab === 'progress' || tab === 'settings') {
      state.tab = tab
      render()
    }
  }
  else if (action === 'export-reviews') void exportReviews()
  else if (action === 'import-reviews') document.querySelector<HTMLInputElement>('#review-import')?.click()
})

let offlineReadyTimer: ReturnType<typeof setTimeout> | null = null

if (navigator.storage?.persist !== undefined) {
  void navigator.storage.persist().catch(() => undefined)
}

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault()
  pwa.installPrompt = event as InstallPromptEvent
  renderPwaBanner()
})

window.addEventListener('appinstalled', () => {
  pwa.installPrompt = null
  pwa.installDismissed = true
  renderPwaBanner()
})

window.addEventListener('beforeunload', (event) => {
  if (state.screen === 'session' && state.session !== null) {
    event.preventDefault()
    event.returnValue = ''
  }
})

window.addEventListener('keydown', (event) => {
  if (state.screen !== 'session' || state.session === null) return
  const target = event.target as HTMLElement | null
  if (target?.matches('input, textarea, select, [contenteditable="true"]')) return

  if (event.key === 'Escape') {
    event.preventDefault()
    void exitSession()
    return
  }

  if (event.key === 'Enter') {
    const button = document.querySelector<HTMLButtonElement>('#session-action')
    if (button !== null && !button.disabled) {
      event.preventDefault()
      button.click()
    }
    return
  }

  if (/^[1-4]$/.test(event.key)) {
    const rating = ['again', 'hard', 'good', 'easy'][Number(event.key) - 1]
    const ratingButton = document.querySelector<HTMLElement>(`[data-session="rate"][data-value="${rating}"]`)
    if (ratingButton !== null) {
      event.preventDefault()
      ratingButton.click()
      return
    }
    const options = document.querySelectorAll<HTMLElement>(
      '[data-session="choice"], [data-session="multi"], [data-session="reading-choice"], [data-session="reading-tf"]',
    )
    const option = options[Number(event.key) - 1]
    if (option !== undefined) {
      event.preventDefault()
      option.click()
    }
  }
})

document.addEventListener('click', (event) => {
  const button = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-pwa]')
  if (!button) return

  const action = button.dataset.pwa
  if (action === 'update') {
    // 答题中不打断；等本轮结束回到今日时再提示。
    if (state.screen === 'session') {
      pwa.deferred = true
      renderPwaBanner()
      return
    }
    pwa.needRefresh = false
    pwa.deferred = false
    renderPwaBanner()
    void updateSW?.(true)
  } else if (action === 'dismiss') {
    if (pwa.needRefresh) pwa.needRefresh = false
    else if (pwa.installPrompt !== null) pwa.installDismissed = true
    else pwa.offlineReady = false
    renderPwaBanner()
  } else if (action === 'install') {
    const prompt = pwa.installPrompt
    if (prompt === null) return
    pwa.installPrompt = null
    renderPwaBanner()
    void prompt.prompt().catch(() => undefined)
  }
})

updateSW = registerSW({
  onNeedRefresh() {
    pwa.needRefresh = true
    pwa.deferred = state.screen === 'session'
    renderPwaBanner()
  },
  onOfflineReady() {
    pwa.offlineReady = true
    renderPwaBanner()
    if (offlineReadyTimer !== null) clearTimeout(offlineReadyTimer)
    offlineReadyTimer = setTimeout(() => {
      pwa.offlineReady = false
      renderPwaBanner()
    }, 5000)
  },
  onRegisterError(error) {
    console.error('Service Worker 注册失败', error)
  },
})

renderPwaBanner()

if (state.local) {
  state.source = localSource
  void refresh(false)
} else if (state.config !== null) {
  state.source = makeGiteeSource(state.config)
  void refresh(false)
} else {
  state.screen = 'setup'
  render()
}
